import {del, get, post, put} from './http';

// Coaching points and delivery observations a reviewer leaves on a submission after it has been
// through review. Distinct from a QA review item: many per submission, and they carry no
// true/false-positive verdict, so they never move analyst accuracy reporting.
export type SubmissionComment = {
  id: string;
  submission_id: string;
  author_email: string;
  // Who the feedback is *for* - snapshotted from the submission's created_by when the comment was
  // written, so it stays pointed at that analyst even if the submission is later amended.
  analyst_email: string;
  body: string;
  created_at: string;
  edited_at: string | null;
  acknowledged_at: string | null;
};

// The dashboard card stands alone with no submission row around it, so it carries enough of the
// submission for the analyst to recognise which one is being commented on.
export type SubmissionCommentFeedback = {
  comment: SubmissionComment;
  policy_violation: string;
  risks: string[];
  display_name: string | null;
  item_external_id: string | null;
  source_url: string | null;
};

export type SubmissionCommentPayload = {
  body: string;
};

export const submissionCommentsApi = {
  list: async (submissionId: string) =>
    get<SubmissionComment[]>(`/submissions/${submissionId}/comments`),

  create: async (submissionId: string, payload: SubmissionCommentPayload) =>
    post<SubmissionCommentPayload, SubmissionComment>(
      `/submissions/${submissionId}/comments`,
      payload
    ),

  update: async (submissionId: string, id: string, payload: SubmissionCommentPayload) =>
    put<SubmissionCommentPayload, SubmissionComment>(
      `/submissions/${submissionId}/comments/${id}`,
      payload
    ),

  remove: async (submissionId: string, id: string) =>
    del(`/submissions/${submissionId}/comments/${id}`),

  // Scoped server-side to the caller's own unacknowledged comments - no query params needed.
  myFeedback: async () => get<SubmissionCommentFeedback[]>('/submission-comments/my-feedback'),

  acknowledge: async (id: string) => post<undefined>(`/submission-comments/${id}/acknowledge`)
};
